import { prefillConnection } from '@/adapter/applicationLoader';
import { Application } from '@/types/application';
import { BackendNode } from '@/types/backendNode';
import {
  ClipboardData,
  ClipboardDataType,
  ClipboardNode,
  ClipboardSubroutineData,
  ClipboardSubroutineValue
} from '@/types/clipboard';
import { Connection, IndexPortOption } from '@/types/connection';
import { Detail } from '@/types/detail';
import { EventHandler, EventName } from '@/types/eventhandler';
import { Layout } from '@/types/layout';
import { Metadata } from '@/types/metadata/metadata';
import { Subroutine, SubroutineFolder } from '@/types/subroutine';
import { Variable } from '@/types/variable';
import { findStartblock, isStartBlock } from '@/util/blocks';
import { getHighestNumber } from '@/util/number';
import { ConfirmOptions, ConfirmResult } from 'material-ui-confirm';
import { findSubroutine, getNewBlockId, setOrReplaceStartblock } from './application';
import { isClipboardData, isNumber } from './typeGuards';

/**
 * Generates new IDs for copied nodes.
 *
 * @param {string[]} selectedNodeIds - IDs of the selected nodes.
 * @returns {Record<string, number>} - A mapping from old IDs to new IDs.
 */
const generateNewIDs = (selectedNodeIds: string[]): Record<string, number> => {
  const idMapping: Record<string, number> = {};
  let newId = Number.MAX_SAFE_INTEGER;
  for (const oldId of selectedNodeIds) {
    idMapping[oldId] = newId;
    newId--;
  }
  return idMapping;
};

/**
 * Updates the connections to point to new IDs.
 *
 * @param {Record<string, Record<string, Connection[]>>} filteredConnections - The filtered connections.
 * @param {Record<string, number>} idMapping - A mapping from old IDs to new IDs.
 * @returns {Record<string, Record<string, Connection[]>>} - The updated connections.
 */
const updateConnections = (
  filteredConnections: Record<string, Connection[]>,
  idMapping: Record<string, number>
): Record<string, Connection[]> => {
  const updatedConnections = structuredClone(filteredConnections);
  for (const [srcId, connections] of Object.entries(filteredConnections)) {
    const newSrcId = idMapping[srcId];
    updatedConnections[newSrcId] = [];
    connections?.forEach((conn, index) => {
      updatedConnections[newSrcId][index] = {
        ...conn,
        dst: isNumber(conn.dst) ? (idMapping[conn.dst] ?? null) : null
      };
    });
  }
  return updatedConnections;
};

/**
 * Updates the copied nodes with new IDs and updated connections.
 *
 * @param {Object[]} copies - An array of node, detail, and connection objects to update.
 * @param {string[]} selectedNodeIds - IDs of the selected nodes.
 * @param {Record<string, number>} idMapping - A mapping from old IDs to new IDs.
 * @param {Record<string, Connection[]>} updatedConnections - The updated connections.
 * @returns {Object[]} - An array of updated node, detail, and connection objects.
 */
const updateCopies = (
  copies: {
    node: BackendNode;
    details: Detail;
    layout: Layout | undefined;
    connections: Connection[];
    portoptions: IndexPortOption;
    event_handlers: Record<EventName, EventHandler> | undefined;
  }[],
  selectedNodeIds: string[],
  idMapping: Record<string, number>,
  updatedConnections: Record<string, Connection[]>
): ClipboardNode[] => {
  return copies.map((copy, index) => {
    const newId = idMapping[selectedNodeIds[index]];
    if (copy.portoptions) {
      return {
        ID: newId,
        ...copy,
        node: { ...copy.node },
        details: { ...copy.details },
        connections: [...updatedConnections[newId]],
        portoptions: { ...copy.portoptions },
        event_handlers: { ...copy.event_handlers }
      };
    } else {
      return {
        ID: newId,
        ...copy,
        node: { ...copy.node },
        details: { ...copy.details },
        connections: [...updatedConnections[newId]],
        event_handlers: { ...copy.event_handlers }
      };
    }
  });
};

/**
 * Updates nodes before they are copied to the clipboard.
 *
 * @param {Application} application - The application object containing nodes, details, and connections.
 * @param {string[]} selectedNodeIds - An array of node IDs that need to be copied.
 * @returns {ClipboardNode[]} - An array of ClipboardNodes that contain the nodes to be copied.
 */
export const getUpdatedClipboardNodes = (application: Application, selectedNodeIds: string[]): ClipboardNode[] => {
  const { nodes, details, layouts, connections, portoptions, event_handlers } = application.blocks;

  const filteredConnections = Object.fromEntries(selectedNodeIds.map((nodeId) => [[nodeId], connections[nodeId]]));

  const idMapping: Record<string, number> = generateNewIDs(selectedNodeIds);

  const updatedConnections = updateConnections(filteredConnections, idMapping);

  const copies = selectedNodeIds.map((nodeId) => {
    const appBlock = nodes[nodeId];
    const appDetails = details[nodeId];
    const appConnections = connections[nodeId];
    const appPortoptions = portoptions[nodeId];
    const appEventHandlers = event_handlers ? event_handlers[nodeId] : undefined;
    return {
      node: appBlock,
      details: appDetails,
      layout: layouts[nodeId],
      connections: appConnections,
      portoptions: appPortoptions,
      event_handlers: appEventHandlers
    };
  });

  const updatedCopies = updateCopies(copies, selectedNodeIds, idMapping, updatedConnections);

  return updatedCopies;
};

export const copyNodesToClipboard = (
  application: Application,
  selectedSubroutineId: number | undefined,
  selectedNodeIds: string[]
): Promise<number> => {
  const updatedCopies = getUpdatedClipboardNodes(application, selectedNodeIds);
  const currentSubroutine = findSubroutine(application.subroutines, selectedSubroutineId);

  updatedCopies.forEach((copy) => {
    if (isStartBlock(copy.node)) {
      copy.args = currentSubroutine?.args;
      copy.argBindings = currentSubroutine?.arg_bindings;
      copy.returnTypes = currentSubroutine?.return_types;
    }
  });

  console.debug('copied to clipboard', updatedCopies);
  const clipboardData: ClipboardData = { type: 'nodes', nodes: updatedCopies };

  return copyToClipboard(clipboardData, clipboardData.nodes.length);
};

export const copySubroutinesToClipboard = (
  application: Application,
  subroutines: Subroutine[],
  folders: SubroutineFolder[]
): Promise<number> => {
  const values: ClipboardSubroutineValue[] = [];
  subroutines.forEach((subroutine) => {
    const updatedCopies = getUpdatedClipboardNodes(
      application,
      subroutine.nodes.map((node) => node.toString())
    );

    updatedCopies.forEach((copy) => {
      if (isStartBlock(copy.node)) {
        copy.args = subroutine?.args;
        copy.argBindings = subroutine?.arg_bindings;
        copy.returnTypes = subroutine?.return_types;
      }
    });

    values.push({
      subroutine: subroutine,
      nodes: updatedCopies,
      variables: application.variables.local[subroutine.id] ?? []
    });
  });

  const clipboardData: ClipboardData = { type: 'subroutines', data: values, folders: folders };

  return copyToClipboard(clipboardData, values.length);
};

/**
 * Copies selected nodes from an application to the clipboard.
 * Resolves a Promise upon successful copy to the clipboard.
 *
 * @param {ClipboardData} clipboardData - The data that is being copied.
 * @param {number} itemLength - The amount of items that are being copied to clipboard.
 * @returns {Promise<number>} A promise that resolves when the clipboard operation is successful and rejects when it fails.
 */
export const copyToClipboard = (clipboardData: ClipboardData, itemLength: number): Promise<number> => {
  return new Promise((resolve, reject) => {
    console.debug('copied to clipboard', clipboardData);
    navigator.clipboard
      .writeText(JSON.stringify(clipboardData))
      .then(() => {
        resolve(itemLength);
      })
      .catch((reason) => {
        console.error('copy failed', reason);
        reject(reason);
      });
  });
};

/**
 * Asynchronously checks if the clipboard contains valid ClipboardData.
 *
 * @returns {Promise<boolean>} A promise resolving to true if clipboard contains valid ClipboardData, otherwise false.
 */
export const hasClipboardData = (type: ClipboardDataType): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    navigator.clipboard
      .readText()
      .then((value) => {
        try {
          const parsedData = JSON.parse(value);
          if (parsedData.type !== type) {
            resolve(false);
            return;
          }
          console.debug(parsedData);
          resolve(isClipboardData(parsedData));
        } catch {
          reject('Not ClipboardData in clipboard');
        }
      })
      .catch((reason) => reject(reason));
  });
};

/**
 * Asynchronously fetches and returns ClipboardData from the user's clipboard.
 *
 * @returns {Promise<ClipboardData>} A promise that resolves with the ClipboardData if it exists and is valid.
 * @throws Will reject the promise if:
 * - Failed to read from the clipboard.
 * - The clipboard data is not valid ClipboardData.
 */
export const getClipboardData = async (): Promise<ClipboardData | undefined> => {
  const value = await navigator.clipboard.readText();

  try {
    const parsedData = JSON.parse(value);
    if (isClipboardData(parsedData)) {
      return parsedData;
    }
  } catch {
    // Clipboard held something that isn't JSON — fall through to the not-ClipboardData path.
  }

  console.log('Not ClipboardData in clipboard');
};

/**
 * Updates the IDs of blocks within the given ClipboardData, ensuring they are unique within the given application.
 *
 * @param {ClipboardNode[]} clipboardNodes - The ClipboardData object containing nodes, details, and connections.
 * @param {Application} application - The application object containing nodes, details, and connections.
 * @returns {ClipboardNode[]} - Updated ClipboardData with unique IDs.
 */
export const updateClipboardDataIds = (application: Application, clipboardNodes: ClipboardNode[]): ClipboardNode[] => {
  // Use getNewBlockId to start from the next available ID in the application
  let newId = getNewBlockId(application);
  const idMapping: Record<number, number> = {};

  // Create a new ID mapping
  for (const block of clipboardNodes) {
    const oldId = block.ID;
    idMapping[oldId] = newId;
    newId++;
  }

  // Use the ID mapping to update IDs in clipboardNodes
  return clipboardNodes.map((block) => ({
    ...block,
    ID: idMapping[block.ID],
    // Also update connections if they exist and contain IDs
    connections: updateConnections({ [block.ID]: block.connections }, idMapping)[idMapping[block.ID]]
  }));
};

export const getUpdatedPasteData = (
  updatedApplication: Application,
  metadata: Metadata,
  subroutineId: number,
  updatedData: ClipboardNode[],
  variables?: Variable[],
  subroutineIds?: Record<string, number>
) => {
  const newNodes = Object.fromEntries(updatedData.map((node) => [node.ID, node.node]));
  const newDetails = Object.fromEntries(updatedData.map((node) => [node.ID, node.details]));
  const newLayouts: Record<string, Layout> = {};
  updatedData.forEach((node) => {
    if (node.layout) newLayouts[node.ID] = node.layout;
  });
  const newConnections = Object.fromEntries(updatedData.map((node) => [node.ID, node.connections]));
  const newPortoptions = Object.fromEntries(
    updatedData.filter((data) => data.portoptions).map((node) => [node.ID, node.portoptions])
  );
  const newEventHandlers = Object.fromEntries(updatedData.map((node) => [node.ID, node.event_handlers]));
  const newArgs = updatedData.find((data) => data.args);
  const newReturnTypes = updatedData.find((data) => data.returnTypes);

  updatedApplication.blocks.nodes = {
    ...updatedApplication.blocks.nodes,
    ...newNodes
  };
  updatedApplication.blocks.details = {
    ...updatedApplication.blocks.details,
    ...newDetails
  };
  updatedApplication.blocks.layouts = {
    ...updatedApplication.blocks.layouts,
    ...newLayouts
  };
  updatedApplication.blocks.connections = {
    ...updatedApplication.blocks.connections,
    ...newConnections
  };
  if (Object.keys(newPortoptions).length) {
    updatedApplication.blocks.portoptions = {
      ...updatedApplication.blocks.portoptions,
      ...newPortoptions
    };
  }
  if (newEventHandlers) {
    Object.entries(newEventHandlers).map(([_, eventHandlerRecord]) =>
      Object.entries(eventHandlerRecord).map(([_, eventHandler]) => {
        if (subroutineIds && subroutineIds[eventHandler.eventstart])
          eventHandler.eventstart = subroutineIds[eventHandler.eventstart];
      })
    );

    updatedApplication.blocks.event_handlers = {
      ...updatedApplication.blocks.event_handlers,
      ...newEventHandlers
    };
  }

  //only for subroutines
  if (variables) updatedApplication.variables.local[subroutineId] = variables;

  const currentSubroutine = findSubroutine(updatedApplication.subroutines, subroutineId);
  if (currentSubroutine) {
    if (newArgs?.args && newArgs.argBindings) {
      currentSubroutine.args = newArgs.args;
      currentSubroutine.arg_bindings = newArgs.argBindings;
    }

    if (newReturnTypes?.returnTypes) {
      currentSubroutine.return_types = newReturnTypes.returnTypes;
    }

    currentSubroutine.nodes.push(
      ...updatedData.filter((node) => !currentSubroutine.nodes.includes(node.ID)).map((node) => node.ID)
    );
  }

  const finalApplication = updatedData.reduce((accApplication, clipNode) => {
    return prefillConnection(accApplication, metadata, clipNode.ID.toString()) || accApplication;
  }, updatedApplication);

  return finalApplication;
};

export const handlePasteNodesIntoApplication = (
  application: Application,
  setApplication: (newApplication: Application, isNewApplication?: boolean) => void,
  metadata: Metadata,
  subroutineId: number,
  clipboardNodes: ClipboardNode[],
  setUpdateNodes: (updateNodes: string[]) => void,
  setSelectedNodeIds: (ids: string[]) => void,
  confirm?: (options?: ConfirmOptions | undefined) => Promise<ConfirmResult>
) => {
  const updatedData = updateClipboardDataIds(application, clipboardNodes);
  const updatedApplication = getUpdatedPasteData(structuredClone(application), metadata, subroutineId, updatedData);
  const selectedIds = updatedData.map((data) => data.ID.toString());

  const startBlockNode = updatedData.find((data) => isStartBlock(data.node));
  if (startBlockNode && confirm) {
    setOrReplaceStartblock(startBlockNode.ID, updatedApplication, subroutineId, confirm).then(
      (newApplication: Application) => setApplication(newApplication)
    );
  } else {
    setApplication(updatedApplication);
  }
  setUpdateNodes(selectedIds);
  setSelectedNodeIds(selectedIds);
};

export const handlePasteSubroutinesIntoApplication = (
  application: Application,
  metadata: Metadata,
  clipboardData: ClipboardSubroutineData,
  folderId: number | undefined
) => {
  const subroutineIds: Record<string, number> = {};
  const folderIds: Record<string, number> = {};

  clipboardData.folders.forEach((folder, index) => {
    const newFolderId = getHighestNumber(application.folders ?? [], 'id') + index + 1;
    folderIds[folder.id] = newFolderId;
  });

  clipboardData.data.forEach((data, index) => {
    const newSubroutineId = getHighestNumber(application.subroutines, 'id') + index + 1;
    subroutineIds[data.subroutine.id] = newSubroutineId;
  });

  const selectedId = clipboardData.folders.length
    ? folderIds[clipboardData.folders[0].id]
    : subroutineIds[clipboardData.data[0].subroutine.id];

  clipboardData.data.forEach((data) => {
    const newSubroutineId = subroutineIds[data.subroutine.id];
    const updatedNodes = updateClipboardDataIds(application, data.nodes);
    const currentFolder = data.subroutine.folder ? folderIds[data.subroutine.folder] : folderId;

    const subroutineLabelExists = application.subroutines
      .filter((sub) => sub.folder === currentFolder)
      .find((sub) => sub.label === data.subroutine.label);

    application.subroutines.push({
      id: newSubroutineId,
      startblock: data.subroutine.startblock,
      args: data.subroutine.args,
      arg_bindings: data.subroutine.arg_bindings,
      event_args: data.subroutine.event_args,
      event_arg_bindings: data.subroutine.event_arg_bindings,
      label: data.subroutine.label + (subroutineLabelExists ? ' - copy' : ''),
      nodes: updatedNodes.map((node) => node.ID),
      return_types: data.subroutine.return_types,
      folder: currentFolder
    });

    application = getUpdatedPasteData(
      structuredClone(application),
      metadata,
      newSubroutineId,
      updatedNodes,
      data.variables,
      subroutineIds
    );

    updatedNodes.forEach((item) => {
      if (item.node.type === 'Gosub' || item.node.type === 'RunTask') {
        const substartId = item.details['substart'];
        application.blocks.details[item.ID]['substart'] = isNumber(substartId)
          ? (subroutineIds[substartId] ?? substartId)
          : substartId;
      }
    });
  });

  // Reset startblock to the new IDs
  application.subroutines.forEach((subroutine, index) => {
    const startblock = findStartblock(subroutine.nodes, application.blocks, metadata);
    if (isNumber(startblock)) {
      application.subroutines[index].startblock = startblock;
    }
  });

  const appFolders = application.folders ?? [];

  if (clipboardData.folders.length) {
    const newFolders = clipboardData.folders.map((folder) => {
      return {
        id: folderIds[folder.id],
        parentId: folderIds[folder.parentId],
        label: folder.label
      };
    });

    const folderLabelExists = application.folders
      ?.filter((appFolder) => appFolder.parentId === (folderId ?? 0))
      ?.find((appFolder) => appFolder.label === newFolders[0].label);
    if (folderLabelExists) {
      newFolders[0].label += ' - copy';
    }
    newFolders[0].parentId = folderId ?? 0;
    appFolders.push(...newFolders);
  }

  return { newApplication: { ...application, folders: appFolders }, selectedId };
};
